<script>
  import { onMount } from 'svelte';
  import { authStore } from '../stores/authStore.js';
  import { fade, slide } from 'svelte/transition';
  import { flip } from 'svelte/animate';
  import { quintOut } from 'svelte/easing';
  import { apiService } from '../services/apiService.js';

  // Component state
  let customLinks = [];
  let isLoading = true;
  let error = null;
  let isCreatingNew = false;
  let isEditing = false;
  let currentEditId = null;

  // Form state
  let name = '';
  let trackingId = '';
  let promptId = '';
  let availablePrompts = [];
  let isLoadingPrompts = false;
  let assignPrompt = false;
  let promptSearchQuery = '';
  let promptSearchResults = [];
  let promptSearchLoading = false;
  let showSearchTips = false;

  // Add a variable to store selected prompt details
  let selectedPromptDetails = null;

  // Fetch user's custom links
  async function fetchCustomLinks() {
    isLoading = true;
    error = null;
    
    try {
      const response = await fetch('/api/custom-links', {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch custom links');
      }
      
      const data = await response.json();
      console.log('Fetched custom links:', data);
      customLinks = Array.isArray(data) ? data : [];
    } catch (err) {
      console.error('Error fetching custom links:', err);
      error = err.message;
      customLinks = []; // Ensure customLinks is always an array
    } finally {
      isLoading = false;
    }
  }

  // Fetch available prompts for selection
  async function fetchPrompts() {
    isLoadingPrompts = true;
    try {
      const response = await fetch('/api/prompts');
      if (!response.ok) throw new Error('Failed to fetch prompts');
      const prompts = await response.json();
      availablePrompts = prompts;
    } catch (err) {
      console.error('Error fetching prompts:', err);
      // Ensure availablePrompts is always an array even on error
      availablePrompts = [];
    } finally {
      isLoadingPrompts = false;
    }
  }

  // Search prompts with fallback
  async function searchPrompts(event) {
    if (event && (event.type === 'submit' || (event.type === 'keydown' && event.key === 'Enter'))) {
      event.preventDefault();
    }

    if (!promptSearchQuery.trim()) {
      promptSearchResults = [];
      return;
    }
    
    promptSearchLoading = true;
    try {
      console.log('Searching for prompts with query:', promptSearchQuery);
      
      // Use the apiService to search prompts
      const results = await apiService.searchPrompts(promptSearchQuery.trim());
      console.log('Search results:', results);
      promptSearchResults = Array.isArray(results) ? results : [];
      
      if (promptSearchResults.length === 0) {
        console.log('No results found, attempting to fetch all prompts as fallback');
        // Fallback: fetch all prompts and filter locally
        await fetchPrompts();
        
        if (availablePrompts && availablePrompts.length > 0) {
          const query = promptSearchQuery.toLowerCase();
          promptSearchResults = availablePrompts.filter(prompt => 
            (prompt.bigQuestion && prompt.bigQuestion.toLowerCase().includes(query)) || 
            (prompt.promptScript && prompt.promptScript.toLowerCase().includes(query))
          );
          console.log('Filtered prompts locally:', promptSearchResults);
        }
      }
    } catch (err) {
      console.error('Error searching prompts:', err);
      promptSearchResults = [];
    } finally {
      promptSearchLoading = false;
    }
  }

  // Toggle prompt assignment
  function togglePromptAssignment() {
    assignPrompt = !assignPrompt;
    if (assignPrompt && availablePrompts.length === 0) {
      fetchPrompts();
    } else if (!assignPrompt) {
      promptId = '';
    }
  }

  // Check if a tracking ID already exists
  async function checkTrackingIdExists(trackingId) {
    try {
      const response = await fetch(`/api/custom-links/check-id?trackingId=${encodeURIComponent(trackingId)}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.error('Error checking tracking ID:', response.status);
        // If API endpoint doesn't exist, fall back to client-side check
        return isTrackingIdUsedInCurrentLinks(trackingId);
      }
      
      const data = await response.json();
      return data.exists;
    } catch (err) {
      console.error('Error checking tracking ID:', err);
      // Fall back to client-side check
      return isTrackingIdUsedInCurrentLinks(trackingId);
    }
  }
  
  // Client-side check if tracking ID exists in current list
  function isTrackingIdUsedInCurrentLinks(trackingId) {
    if (!customLinks || !Array.isArray(customLinks)) return false;
    // Don't count the current link being edited
    return customLinks.some(link => 
      link.trackingId === trackingId && 
      (!isEditing || link._id !== currentEditId)
    );
  }

  // Generate a unique random tracking ID
  async function generateTrackingId() {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const length = 8;
    let attempts = 0;
    const maxAttempts = 10;
    
    while (attempts < maxAttempts) {
      // Generate a random ID
      const id = Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
      
      // Check if it's unique
      const exists = await checkTrackingIdExists(id);
      
      if (!exists) {
        return id; // Return the unique ID
      }
      
      attempts++;
    }
    
    // If we couldn't generate a unique ID after max attempts, add a timestamp
    return Array.from({ length: 5 }, () => chars[Math.floor(Math.random() * chars.length)]).join('') + 
           Date.now().toString().slice(-3);
  }

  // Create a new custom link with enhanced debugging and error handling
  async function createCustomLink() {
    try {
      // Clear any previous errors
      error = null;

      // Validate required fields
      if (!name.trim()) {
        error = 'Link name is required';
        return;
      }
      
      if (!trackingId.trim()) {
        error = 'Tracking ID is required';
        return;
      }
      
      // Validate tracking ID format manually
      if (!/^[a-zA-Z0-9-]+$/.test(trackingId)) {
        error = 'Tracking ID can only contain letters, numbers, and hyphens';
        return;
      }
      
      // Check if tracking ID already exists
      const idExists = await checkTrackingIdExists(trackingId);
      if (idExists) {
        error = 'This tracking ID is already in use. Please choose a different one.';
        return;
      }
      
      // Check auth store state first
      if (!$authStore.isAuthenticated || !$authStore.user) {
        error = 'Please log in to create a custom link';
        console.log('Auth store state:', $authStore);
        return;
      }

      // Verify session is active before proceeding
      try {
        const sessionCheck = await fetch('/auth/check-session', {
          credentials: 'include'
        });
        const sessionData = await sessionCheck.json();
        
        if (!sessionData.isAuthenticated) {
          error = 'Your session has expired. Please log in again.';
          console.log('Session check failed:', sessionData);
          return;
        }
      } catch (sessionError) {
        console.error('Error checking session:', sessionError);
        error = 'Unable to verify session. Please try again.';
        return;
      }
      
      // Prepare payload with user information
      const payload = {
        name: name.trim(),
        trackingId: trackingId.trim(),
        promptId: promptId || null
      };
      
      console.log('Creating custom link with payload:', payload);
      console.log('Auth state:', {
        isAuthenticated: $authStore.isAuthenticated,
        user: $authStore.user
      });

      const response = await fetch('/api/custom-links', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      console.log('API response status:', response.status);
      console.log('API response headers:', Object.fromEntries([...response.headers.entries()]));

      const responseText = await response.text();
      console.log('API response text:', responseText);

      let data;
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch (jsonError) {
        console.error('Failed to parse response as JSON:', jsonError);
        throw new Error(`Invalid response format. Status: ${response.status}`);
      }

      if (!response.ok) {
        if (response.status === 401) {
          error = 'Your session has expired. Please log in again.';
          return;
        }
        throw new Error(data.error || `Failed to create custom link (${response.status})`);
      }

      console.log('Custom link created successfully:', data);
      resetForm();
      await fetchCustomLinks();

    } catch (err) {
      console.error('Error creating custom link:', err);
      error = err.message || 'Failed to create custom link';
    }
  }

  // Update an existing custom link
  async function updateCustomLink() {
    try {
      // Validate required fields
      if (!name.trim()) {
        error = 'Link name is required';
        return;
      }
      
      if (!trackingId.trim()) {
        error = 'Tracking ID is required';
        return;
      }
      
      if (!currentEditId) {
        error = 'Invalid link ID';
        return;
      }
      
      // Validate tracking ID format
      if (!/^[a-zA-Z0-9-]+$/.test(trackingId)) {
        error = 'Tracking ID can only contain letters, numbers, and hyphens';
        return;
      }
      
      // Find the current link being edited
      const currentLink = customLinks.find(link => link._id === currentEditId);
      
      // If tracking ID has changed, check if the new one already exists
      if (currentLink && currentLink.trackingId !== trackingId) {
        const idExists = await checkTrackingIdExists(trackingId);
        if (idExists) {
          error = 'This tracking ID is already in use. Please choose a different one.';
          return;
        }
      }
      
      const response = await fetch(`/api/custom-links/${currentEditId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name,
          trackingId,
          promptId: promptId || null
        }),
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Check for specific error messages from the server
        if (data.error && data.error.includes('already exists')) {
          error = 'This tracking ID is already in use. Please choose a different one.';
          return;
        }
        throw new Error(data.error || 'Failed to update custom link');
      }
      
      console.log('Updated custom link:', data);
      
      // Reset form and fetch updated list
      resetForm();
      await fetchCustomLinks();
    } catch (err) {
      console.error('Error updating custom link:', err);
      error = err.message;
    }
  }

  // Delete a custom link
  async function deleteCustomLink(id) {
    if (!confirm('Are you sure you want to delete this custom link?')) {
      return;
    }
    
    if (!id) {
      error = 'Invalid link ID';
      return;
    }
    
    try {
      const response = await fetch(`/api/custom-links/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'include'
      });
      
      // Handle both successful deletion response formats
      if (response.ok) {
        try {
          const data = await response.json();
          console.log('Deleted custom link:', data);
        } catch (e) {
          // Some DELETE responses might not return JSON
          console.log('Deleted custom link successfully');
        }
        
        // Refresh the list
        await fetchCustomLinks();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete custom link');
      }
    } catch (err) {
      console.error('Error deleting custom link:', err);
      error = err.message;
    }
  }

  // Handle form submission
  function handleSubmit() {
    if (isEditing) {
      updateCustomLink();
    } else {
      createCustomLink();
    }
  }

  // Reset form state
  function resetForm() {
    name = '';
    trackingId = '';
    promptId = '';
    isCreatingNew = false;
    isEditing = false;
    currentEditId = null;
    error = null;
  }

  // Start editing a custom link
  function editCustomLink(link) {
    name = link.name;
    trackingId = link.trackingId;
    promptId = link.promptId || '';
    isEditing = true;
    isCreatingNew = true;
    currentEditId = link._id;
    
    // Scroll to form
    setTimeout(() => {
      document.getElementById('custom-link-form').scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }

  // Create new custom link form
  async function showCreateForm() {
    resetForm();
    isCreatingNew = true;
    
    // Generate a unique tracking ID
    try {
      trackingId = await generateTrackingId();
    } catch (err) {
      console.error('Error generating tracking ID:', err);
      // Fallback to basic generation if async check fails
      trackingId = Array.from({ length: 8 }, () => 
        'abcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(Math.random() * 36)]
      ).join('');
    }
  }

  // Generate a shareable URL for a custom link
  function getShareableUrl(trackingId) {
    return `https://datacreative.app/tool?tid=${trackingId}`;
  }

  // Copy URL to clipboard
  function copyToClipboard(text) {
    navigator.clipboard.writeText(text);
  }

  // Add a function to update selected prompt details when promptId changes
  function updateSelectedPromptDetails() {
    if (promptId) {
      // Find the selected prompt details
      selectedPromptDetails = 
        [...(availablePrompts || []), ...(promptSearchResults || [])]
          .find(p => p && p._id === promptId);
      
      if (!selectedPromptDetails && promptId) {
        // If not found in current lists, try to fetch it
        fetchPromptDetails(promptId);
      }
    } else {
      selectedPromptDetails = null;
    }
  }

  // Add a watcher for promptId changes
  $: if (promptId !== undefined) updateSelectedPromptDetails();

  // Add the fetchPromptDetails function
  async function fetchPromptDetails(id) {
    try {
      const response = await fetch(`/api/prompts/${id}`);
      if (response.ok) {
        const prompt = await response.json();
        selectedPromptDetails = prompt;
      }
    } catch (error) {
      console.error('Error fetching prompt details:', error);
    }
  }

  // Add this function in the script section after the imports
  function handleLogin() {
    authStore.setRedirect(window.location.pathname);
    authStore.loginWithGoogle();
  }

  onMount(() => {
    if ($authStore.isAuthenticated) {
      fetchCustomLinks();
    } else {
      isLoading = false;
    }
  });
</script>

<div class="custom-link-container">
  {#if !$authStore.isAuthenticated}
    <div class="auth-message">
      <p>You need to log in to create and manage custom links.</p>
      <button class="primary-button" on:click={handleLogin}>
        Log In with Google
      </button>
    </div>
  {:else}
    <!-- Link Management UI -->
    <div class="links-management">
      <div class="links-header">
        {#if isCreatingNew}
          <div class="breadcrumb">
            <button class="back-button" on:click={resetForm}>
              <i class="bi bi-arrow-left"></i> Back to Links
            </button>
          </div>
          <h2>{isEditing ? 'Edit Custom Link' : 'Create New Custom Link'}</h2>
        {:else}
          <div class="header-actions">
            <button class="primary-button" on:click={showCreateForm}>
              <i class="bi bi-plus-circle"></i> Create New Link
            </button>
          </div>
        {/if}
      </div>
      
      {#if error}
        <div class="error-message" transition:slide={{duration: 300}}>
          <p>{error}</p>
          <button on:click={() => error = null} aria-label="Dismiss error">
            <i class="bi bi-x"></i>
          </button>
        </div>
      {/if}
      
      {#if isLoading}
        <div class="loading" in:fade={{duration: 300}}>
          <div class="loading-spinner"></div>
          <p>Loading your custom links...</p>
        </div>
      {:else if !Array.isArray(customLinks) || customLinks.length === 0}
        {#if !isCreatingNew}
          <div class="empty-state" transition:fade={{duration: 300}}>
            <div class="empty-state-icon">
              <i class="bi bi-link-45deg"></i>
            </div>
            <p>You don't have any custom links yet.</p>
            <button class="primary-button" on:click={showCreateForm}>
              Create Your First Link
            </button>
          </div>
        {/if}
      {:else if !isCreatingNew}
        <div class="links-list" in:fade={{duration: 300}}>
          {#each customLinks || [] as link}
            {#if link}
              <div class="link-card" transition:fade|local={{duration: 300}}>
                <div class="link-info">
                  <h3>{link.name || 'Unnamed Link'}</h3>
                  <div class="link-details">
                    <div class="detail-item">
                      <span class="detail-label">Tracking ID:</span>
                      <span class="detail-value">{link.trackingId || 'No ID'}</span>
                    </div>
                    <div class="detail-item">
                      <span class="detail-label">Prompt:</span>
                      <span class="detail-value">
                        {#if link.promptId && selectedPromptDetails}
                          {selectedPromptDetails.bigQuestion}
                        {:else}
                          None
                        {/if}
                      </span>
                    </div>
                    <div class="detail-item date-item">
                      <span class="detail-label">Created:</span>
                      <span class="detail-value">
                        {link.createdAt ? new Date(link.createdAt).toLocaleDateString() : 'Unknown date'}
                      </span>
                    </div>
                  </div>
                  <div class="shareable-url">
                    <div class="url-display">
                      <input 
                        type="text" 
                        readonly 
                        value={getShareableUrl(link.trackingId)} 
                        aria-label="Shareable URL"
                      />
                      <button 
                        class="copy-button" 
                        on:click={() => {
                          copyToClipboard(getShareableUrl(link.trackingId));
                          alert('URL copied to clipboard!');
                        }}
                        aria-label="Copy URL to clipboard"
                      >
                        <i class="bi bi-clipboard"></i> Copy
                      </button>
                    </div>
                  </div>
                </div>
                <div class="link-actions">
                  <button class="action-button edit-button" on:click={() => editCustomLink(link)}>
                    <i class="bi bi-pencil"></i> Edit
                  </button>
                  <button class="action-button delete-button" on:click={() => deleteCustomLink(link._id)}>
                    <i class="bi bi-trash"></i> Delete
                  </button>
                </div>
              </div>
            {/if}
          {/each}
        </div>
      {/if}
      
      <!-- Create/Edit Form -->
      {#if isCreatingNew}
        <div class="form-container" id="custom-link-form" transition:slide={{duration: 300}}>
          <form on:submit|preventDefault={handleSubmit}>
            <div class="form-content">
              <div class="form-group">
                <label for="name">Link Name<span class="required">*</span></label>
                <input 
                  type="text" 
                  id="name" 
                  bind:value={name} 
                  placeholder="e.g., My Classroom Link" 
                  required
                  autocomplete="off"
                  class:error={name === '' && error}
                />
                <small>A descriptive name to help you identify this link</small>
              </div>
              
              <div class="form-group">
                <label for="trackingId">Tracking ID<span class="required">*</span></label>
                <div class="input-with-button">
                  <input 
                    type="text" 
                    id="trackingId" 
                    bind:value={trackingId} 
                    placeholder="e.g., classroom123" 
                    required
                    autocomplete="off"
                    class:error={trackingId === '' && error}
                  />
                  <button 
                    type="button" 
                    class="secondary-button" 
                    on:click={async () => {
                      try {
                        const newId = await generateTrackingId();
                        trackingId = newId;
                      } catch (err) {
                        console.error('Error generating tracking ID:', err);
                        error = 'Failed to generate a unique tracking ID. Please try again.';
                      }
                    }}
                    aria-label="Generate random ID"
                  >
                    Generate
                  </button>
                </div>
                <small>Unique identifier for this link (letters, numbers, and hyphens only)</small>
              </div>
              
              <div class="form-group prompt-selection">
                <div class="prompt-toggle">
                  <label class="checkbox-label">
                    <input type="checkbox" bind:checked={assignPrompt} />
                    Pre-select a prompt for users
                  </label>
                  <small>When enabled, users will automatically start with your selected prompt</small>
                </div>
                
                {#if assignPrompt}
                  <div class="prompt-search" transition:slide={{duration: 300}}>
                    <div class="search-input-wrapper">
                      <i class="bi bi-search search-icon"></i>
                      <form on:submit|preventDefault={searchPrompts}>
                        <input 
                          type="text" 
                          id="promptSearch" 
                          bind:value={promptSearchQuery} 
                          placeholder="Enter keywords to search prompts"
                          autocomplete="off"
                          class="search-input"
                        />
                        <button 
                          type="button" 
                          class="search-tips-button" 
                          title="Search Tips"
                          on:click={() => showSearchTips = !showSearchTips}
                        >
                          <i class="bi bi-question-circle"></i>
                        </button>
                        {#if promptSearchQuery}
                          <button 
                            type="button"
                            class="clear-button" 
                            on:click={() => {
                              promptSearchQuery = '';
                              promptSearchResults = [];
                            }}
                            aria-label="Clear search"
                          >
                            <i class="bi bi-x-lg"></i>
                          </button>
                        {/if}
                        <button 
                          type="submit" 
                          class="search-submit"
                          style="display: none;"
                        >
                          Search
                        </button>
                      </form>
                      {#if showSearchTips}
                        <div class="search-tips-dropdown" transition:fade>
                          <h4>Search Strategies</h4>
                          <div class="search-tips-content">
                            <section class="search-tip-section">
                              <h5>Boolean Operators</h5>
                              <ul>
                                <li><code>AND</code>: Match both terms (e.g. <span class="example">"education AND technology"</span>)</li>
                                <li><code>OR</code>: Match either term (e.g. <span class="example">"climate OR weather"</span>)</li>
                                <li><code>NOT</code>: Exclude term (e.g. <span class="example">"politics NOT economy"</span>)</li>
                              </ul>
                            </section>

                            <section class="search-tip-section">
                              <h5>Exact Phrase Matching</h5>
                              <ul>
                                <li><code>"..."</code>: Match exact phrase (e.g. <span class="example">"public health"</span>)</li>
                              </ul>
                            </section>

                            <section class="search-tip-section">
                              <h5>Grouping Terms</h5>
                              <ul>
                                <li><code>()</code>: Group terms (e.g. <span class="example">"(education OR learning) AND digital"</span>)</li>
                              </ul>
                            </section>

                            <section class="search-tip-section">
                              <h5>Wildcards</h5>
                              <ul>
                                <li><code>*</code>: Match any characters (e.g. <span class="example">"environ*"</span> matches environment, environmental, etc.)</li>
                              </ul>
                            </section>
                          </div>
                          <button class="close-tips" on:click={() => showSearchTips = false}>
                            <i class="bi bi-x"></i>
                          </button>
                        </div>
                      {/if}
                    </div>
                    
                    {#if promptSearchLoading}
                      <div class="prompt-loading" transition:fade={{duration: 300}}>
                        <div class="loading-icon-container"><i class="bi bi-arrow-repeat spinner"></i></div>
                        <span>Searching prompts...</span>
                      </div>
                    {:else if promptSearchResults.length > 0}
                      <div class="prompt-results" transition:slide={{duration: 300}}>
                        <div class="results-title">Search Results:</div>
                        <div class="prompt-list">
                          {#each promptSearchResults as prompt}
                            <div 
                              class="prompt-item" 
                              class:selected={promptId === prompt._id}
                              on:click={() => promptId = prompt._id}
                              on:keydown={(e) => e.key === 'Enter' && (promptId = prompt._id)}
                              tabindex="0"
                              role="button"
                              aria-pressed={promptId === prompt._id}
                            >
                              <div class="prompt-title">
                                {prompt.bigQuestion || 'Untitled Prompt'}
                                {#if promptId === prompt._id}
                                  <i class="bi bi-check-circle-fill selected-icon"></i>
                                {/if}
                              </div>
                              <div class="prompt-description">{prompt.promptScript || 'No description available'}</div>
                            </div>
                          {/each}
                        </div>
                      </div>
                    {:else if promptSearchQuery && !promptSearchLoading}
                      <div class="no-results" transition:fade={{duration: 300}}>
                        <i class="bi bi-search"></i>
                        <span>No prompts found. Try different keywords.</span>
                      </div>
                    {/if}
                  </div>
                {/if}
              </div>
            </div>
            
            <div class="form-actions">
              <button 
                type="button" 
                class="cancel-button" 
                on:click={resetForm}
                aria-label="Cancel"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                class="submit-button"
                aria-label={isEditing ? 'Update link' : 'Create link'}
              >
                {isEditing ? 'Update' : 'Create'} Link
              </button>
            </div>
          </form>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .custom-link-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  
  .auth-message {
    text-align: center;
    padding: 2rem;
    background: #f9f9f9;
    border-radius: 0.75rem;
    margin: 2rem auto;
    max-width: 500px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }
  
  .links-header {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    flex-shrink: 0;
    position: sticky;
    top: 0;
    background: white;
    z-index: 5;
    padding: 1rem 0;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .primary-button {
    background: #2563eb;
    color: white;
    border: none;
    padding: 0.6rem 1.2rem;
    border-radius: 0.5rem;
    font-size: 0.9rem;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
  }
  
  .primary-button:hover {
    background: #1d4ed8;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(37, 99, 235, 0.25);
  }
  
  .primary-button:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.3);
  }
  
  .secondary-button {
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    color: #374151;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .secondary-button:hover {
    background: #e5e7eb;
  }
  
  .error-message {
    background: #fee2e2;
    color: #b91c1c;
    padding: 1rem;
    border-radius: 0.5rem;
    margin-bottom: 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 4px rgba(185, 28, 28, 0.1);
    flex-shrink: 0; /* Prevent error message from shrinking */
  }
  
  .error-message button {
    background: transparent;
    border: none;
    color: #b91c1c;
    cursor: pointer;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
  }
  
  .error-message button:hover {
    background: rgba(185, 28, 28, 0.1);
  }
  
  .loading {
    text-align: center;
    padding: 2rem;
    color: #666;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
  
  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-radius: 50%;
    border-top-color: #2563eb;
    animation: spin 1s linear infinite;
  }
  
  .loading-spinner.small {
    width: 20px;
    height: 20px;
    border-width: 2px;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  .empty-state {
    text-align: center;
    padding: 3rem;
    background: #f9f9f9;
    border-radius: 0.75rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
  
  .empty-state-icon {
    font-size: 3rem;
    color: #d1d5db;
  }
  
  /* Scrollable container for the links management area */
  .links-management {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    position: relative;
    padding-bottom: 2rem;
    margin: 0 -20px; /* Counteract parent padding */
    padding: 0 20px; /* Restore padding for content */
  }
  
  .links-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 2rem;
    overflow-y: visible; /* Allow content to expand */
  }
  
  .link-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
    padding: 1.25rem;
    display: flex;
    justify-content: space-between;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    transition: box-shadow 0.2s, transform 0.2s;
  }
  
  .link-card:hover {
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    transform: translateY(-2px);
  }
  
  .link-info {
    flex: 1;
  }
  
  .link-info h3 {
    margin: 0 0 0.75rem 0;
    font-size: 1.25rem;
    color: #111827;
  }
  
  .link-details {
    margin-bottom: 1rem;
  }
  
  .detail-item {
    display: flex;
    margin: 0.5rem 0;
    font-size: 0.95rem;
  }
  
  .detail-label {
    font-weight: 500;
    color: #4b5563;
    width: 100px;
    flex-shrink: 0;
  }
  
  .detail-value {
    color: #1f2937;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .date-item {
    color: #6b7280;
    font-size: 0.85rem;
  }
  
  .shareable-url {
    margin-top: 1rem;
  }
  
  .url-display {
    display: flex;
    gap: 0.5rem;
    background: #f9fafb;
    padding: 0.5rem;
    border-radius: 0.5rem;
    border: 1px solid #e5e7eb;
  }
  
  .url-display input {
    flex: 1;
    padding: 0.5rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    font-size: 0.9rem;
    background: white;
  }
  
  .copy-button {
    background: #10b981;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.9rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    transition: background-color 0.2s;
  }
  
  .copy-button:hover {
    background: #059669;
  }
  
  .copy-button:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.3);
  }
  
  .link-actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-left: 1rem;
  }
  
  .action-button {
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.9rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    transition: all 0.2s;
  }
  
  .edit-button {
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    color: #374151;
  }
  
  .edit-button:hover {
    background: #e5e7eb;
  }
  
  .delete-button {
    background: #fef2f2;
    border: 1px solid #fee2e2;
    color: #991b1b;
  }
  
  .delete-button:hover {
    background: #fee2e2;
  }
  
  .form-container {
    background: #f9fafb;
    border-radius: 0.75rem;
    padding: 1.5rem 1.5rem 0 1.5rem; /* Removed bottom padding - form actions will provide it */
    margin: 0.5rem 0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    display: flex;
    flex-direction: column;
    overflow: visible; /* Allow content to expand normally */
  }
  
  .form-content {
    overflow-y: visible; /* Allow form content to expand */
  }
  
  .form-group {
    margin-bottom: 1.5rem;
  }
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
  }
  
  .required {
    color: #ef4444;
    margin-left: 0.25rem;
  }
  
  small {
    display: block;
    margin-top: 0.5rem;
    color: #6b7280;
    font-size: 0.8rem;
  }
  
  input, select {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    font-size: 1rem;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  
  input:focus, select:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
  
  input.error {
    border-color: #ef4444;
  }
  
  .input-with-button {
    display: flex;
    gap: 0.5rem;
  }
  
  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 2rem;
    padding: 1rem;
    background: #f9fafb;
    border-top: 1px solid #e5e7eb;
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 10;
    box-shadow: 0 -2px 10px rgba(0,0,0,0.05); /* Add shadow for visual separation */
  }
  
  .cancel-button {
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    color: #374151;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .cancel-button:hover {
    background: #e5e7eb;
  }
  
  .submit-button {
    background: #2563eb;
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
  }
  
  .submit-button:hover {
    background: #1d4ed8;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(37, 99, 235, 0.25);
  }
  
  .breadcrumb {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  
  .back-button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #6b7280;
    font-size: 0.875rem;
    font-weight: 500;
    padding: 0.5rem 0;
    background: none;
    border: none;
    cursor: pointer;
    transition: color 0.2s ease;
  }
  
  .back-button:hover {
    color: #374151;
  }
  
  .back-button i {
    font-size: 1rem;
  }
  
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }
  
  .header-actions {
    display: flex;
    justify-content: flex-end;
  }
  
  .prompt-toggle {
    margin-bottom: 1rem;
  }
  
  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
  }
  
  .checkbox-label input {
    width: auto;
    margin: 0;
    accent-color: #2563eb;
  }
  
  .prompt-search {
    background: #f9fafb;
    border-radius: 0.5rem;
    padding: 1rem;
    margin-top: 1rem;
    border: 1px solid #e5e7eb;
    max-height: 400px;
    overflow-y: auto;
  }

  .search-input-wrapper {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 0.25rem;
  }

  .search-input-wrapper form {
    width: 100%;
    position: relative;
    margin: 0;
  }

  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 0.875rem;
    pointer-events: none;
    z-index: 2;
  }

  .search-input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: none;
    font-size: 0.9375rem;
    background: transparent;
  }

  .search-input:focus {
    outline: none;
  }

  .search-tips-button {
    position: absolute;
    right: 2.5rem;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #94a3b8;
    padding: 0.25rem;
    cursor: pointer;
    transition: color 0.2s;
    z-index: 1;
  }

  .search-tips-button:hover {
    color: #2563eb;
  }

  .clear-button {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s;
  }

  .clear-button:hover {
    background-color: #f1f5f9;
    color: #475569;
  }

  .prompt-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 2rem;
    color: #6b7280;
  }

  .loading-icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .spinner {
    animation: spin 1s linear infinite;
  }

  .prompt-results {
    margin-top: 1rem;
  }

  .results-title {
    font-weight: 500;
    margin-bottom: 0.75rem;
    color: #374151;
    font-size: 0.875rem;
  }

  .prompt-list {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    overflow: hidden;
  }

  .prompt-item {
    padding: 0.875rem 1rem;
    border-bottom: 1px solid #e5e7eb;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .prompt-item:last-child {
    border-bottom: none;
  }

  .prompt-item:hover {
    background-color: #f3f4f6;
  }

  .prompt-item.selected {
    background-color: #e0f2fe;
  }

  .prompt-item .prompt-title {
    font-weight: 500;
    color: #1f2937;
    margin-bottom: 0.25rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .prompt-item .prompt-description {
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.4;
  }

  .selected-icon {
    color: #2563eb;
    font-size: 1rem;
  }

  .no-results {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 2rem;
    color: #6b7280;
    font-size: 0.875rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    margin-top: 1rem;
  }

  .search-tips-dropdown {
    position: absolute;
    top: calc(100% + 0.5rem);
    left: 0;
    right: 0;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    padding: 1rem;
    z-index: 50;
    border: 1px solid #e2e8f0;
  }

  .search-tips-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .search-tip-section h5 {
    margin: 0 0 0.5rem 0;
    color: #475569;
    font-size: 0.8125rem;
    font-weight: 600;
  }

  .search-tips-dropdown ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .search-tips-dropdown li {
    margin-bottom: 0.5rem;
    color: #475569;
    font-size: 0.8125rem;
    line-height: 1.4;
  }

  .search-tips-dropdown code {
    background: #f1f5f9;
    padding: 0.125rem 0.25rem;
    border-radius: 4px;
    color: #2563eb;
    font-size: 0.75rem;
    font-family: monospace;
  }

  .search-tips-dropdown .example {
    color: #0f766e;
    font-style: italic;
  }

  .close-tips {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background: none;
    border: none;
    color: #64748b;
    padding: 0.25rem;
    cursor: pointer;
    transition: color 0.2s;
  }

  .close-tips:hover {
    color: #ef4444;
  }
</style>